import {
  type ApiAbsence,
  useAbsenceTypes,
  useAbsences,
  useShifts,
  useUsers,
  useWorkTimeChanges,
} from '@/api/data';
/**
 * Fravær: four jobs, one tab each. The designer's report, item C10: "the current
 * page does four jobs at once". They are the same four; deciding on requests,
 * seeing who is away, checking a balance and reporting sykefravær are separate
 * errands, and stacking them meant the queue you came for was above three
 * screens of other things. Each tab answers one question since the redesign of
 * 2026-09-21 (fravaer/Soknader, Kalender, Saldo, Statistikk); the category of the
 * absence type (fravaer/category.ts) is what colours, filters and counts them.
 *
 * The tab is in the path so it can be linked and survives a reload. Saldo is
 * only a tab when the reader may see the balances; it was already hidden
 * rather than filled with dashes. `?apne=<id>` opens one absence's drawer, the
 * link Varsler gives a cancel request.
 *
 * A fifth tab since #114, Ferieliste: the year's holidays against ferieloven §§ 6 and 7.
 *
 * One drawer for a request, shared by every tab. Writes: approve, reject,
 * create (on behalf of a person, then approved), update, delete on
 * /admin/absence-requests.
 */
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, LoadError } from '@/ui';
import { fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { PageHead, TabPanel, Tabs } from '@/ui/page';
import { addDays } from '@timefly/core';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { type AbsenceDraft, AbsenceDrawer, draftOfAbsence, newAbsenceDraft } from './fravaer/AbsenceDrawer';
import { Ferieliste } from './fravaer/Ferieliste';
import { Kalender } from './fravaer/Kalender';
import { Saldo } from './fravaer/SaldoTab';
import { Soknader } from './fravaer/Soknader';
import { Statistikk } from './fravaer/Statistikk';
import { spanOf } from './fravaer/dates';
import { type Filter, NO_FILTER, teamNames } from './fravaer/filters';
import { cancelIdsOf, queueOf } from './fravaer/queue';
import { tabFrom, tabsFor } from './fravaer/tabs';
import './fravaer/fravaer.css';

export default function Fravaer() {
  const can = useCan();
  // POST, PUT and DELETE /admin/absence-requests are the administrator's; a manager approves
  // and rejects (middleware/rolePolicy.js). The screen offers what the server will accept.
  // Two independent things, and the whole point of separate checkboxes: registering an
  // absence for somebody is absence.manage on the server, and the vacation balances are
  // personnel fields that arrive with people.manage. A role granted one is not offered the
  // other (Codex review, 2026-09-16).
  const mayRegister = can('fravaer.registrere');
  const maySeeBalances = can('ansatte.endre');
  const { tenant } = useTenant();
  const { tab: pathTab } = useParams();
  const [sp, setSp] = useSearchParams();
  const day0 = today();
  const absQ = useAbsences();
  const usersQ = useUsers();
  const typesQ = useAbsenceTypes();
  // the employees' requests to cancel an approved absence (backend cancel-request): rows in the
  // work-time history, which a reader of the timesheet may read; the last four months is plenty
  const cancelQ = useWorkTimeChanges(
    { action: 'absence_cancel_requested', from: `${addDays(day0, -120)}T00:00:00Z` },
    can('timeliste.se'),
  );
  const [draft, setDraft] = useState<AbsenceDraft | null>(null);
  const [filter, setFilter] = useState<Filter>(NO_FILTER);
  // the phone's Filter group, open or closed, kept across the tabs like the filter itself
  const [filtersOpen, setFiltersOpen] = useState(false);

  const users = useMemo(
    () =>
      (usersQ.data ?? [])
        .filter((u) => u.active_status)
        .sort((a, b) => fullName(a).localeCompare(fullName(b), 'nb')),
    [usersQ.data],
  );
  const all = absQ.data ?? [];
  const cancelIds = cancelIdsOf(cancelQ.data ?? [], all);
  const handling = queueOf(all, 'handling', day0, cancelIds);
  // the shifts a decision can collide with: from last week to the end of the last request still
  // open or coming, at most a year out, so a request for next summer shows its conflicts too
  const lastEnd = all
    .filter((a) => a.status !== 'declined')
    .map((a) => spanOf(a).to)
    .filter((d) => d >= day0)
    .sort()
    .pop();
  const near = addDays(day0, 42);
  const far = addDays(day0, 366);
  const shiftsQ = useShifts(
    addDays(day0, -42),
    !lastEnd || lastEnd < near ? near : lastEnd > far ? far : lastEnd,
  );
  const shifts = shiftsQ.data?.shifts ?? [];
  const teams = teamNames(users, tenant?.id);

  const open = (a: ApiAbsence) => setDraft(draftOfAbsence(a));
  const newAt = (uid: number, d: string) => setDraft(newAbsenceDraft(uid, d));
  // ?apne=<id>: one absence's drawer, once the list is there; the parameter is spent on opening
  const apne = Number(sp.get('apne'));
  useEffect(() => {
    if (!apne) return;
    const a = absQ.data?.find((x) => x.id === apne);
    if (!a) return;
    setDraft(draftOfAbsence(a));
    setSp(
      (p) => {
        p.delete('apne');
        return p;
      },
      { replace: true },
    );
  }, [apne, absQ.data, setSp]);

  const tabs = tabsFor(maySeeBalances);
  const tab = tabFrom(pathTab, maySeeBalances);
  const common = {
    all,
    users,
    filter,
    onFilter: setFilter,
    filtersOpen,
    onFiltersOpen: setFiltersOpen,
    teams,
    today: day0,
    tenantId: tenant?.id,
  };

  return (
    <div className="fvr">
      <PageHead
        title={T('Fravær')}
        sub={T('Behandle forespørsler, se hvem som er borte og følg opp saldo.')}
        actions={
          <>
            {/* the type catalogue is edited under Innstillinger; the link only for who may open it */}
            {can('innstillinger.se') && (
              <Link className="btn" to="/innstillinger/typer">
                <Icon name="calendarOff" />
                {T('Fraværstyper')}
              </Link>
            )}
            {mayRegister && (
              <Button tone="pri" onClick={() => users[0] && newAt(users[0].id, day0)}>
                <Icon name="plus" />
                {T('Registrer fravær')}
              </Button>
            )}
          </>
        }
      >
        <Tabs
          label={T('Fravær')}
          value={tab}
          items={tabs.map(([k, n]) => ({
            key: k,
            label: T(n),
            to: `/fravaer/${k}`,
            count: k === 'soknader' ? handling.length : null,
            ...(k === 'soknader' && handling.length > 0
              ? { aria: T('Søknader, {n} krever handling', { n: handling.length }) }
              : {}),
          }))}
        />
      </PageHead>
      {absQ.isError && (
        <LoadError what={T('fravær')} detail={(absQ.error as Error).message} onRetry={() => absQ.refetch()} />
      )}
      <div className={draft ? 'absence-workspace has-detail' : 'absence-workspace'}>
        <TabPanel tab={tab}>
          {tab === 'soknader' && (
            <Soknader
              {...common}
              types={typesQ.data ?? []}
              shifts={shifts}
              cancelIds={cancelIds}
              loading={absQ.isPending && !absQ.isError}
              onOpen={open}
            />
          )}
          {tab === 'kalender' && (
            <Kalender {...common} mayRegister={mayRegister} onOpen={open} onNew={newAt} />
          )}
          {/* the balances are personnel fields: they follow the permission that sends them, not
          the one that registers an absence, and without them the table is columns of dashes,
          so the tab is left out rather than filled with them */}
          {tab === 'saldo' && maySeeBalances && <Saldo {...common} types={typesQ.data ?? []} onOpen={open} />}
          {tab === 'ferieliste' && <Ferieliste {...common} />}
          {tab === 'statistikk' && (
            <Statistikk all={all} people={new Set(users.map((u) => u.id))} today={day0} />
          )}
        </TabPanel>

        <AbsenceDrawer docked draft={draft} shifts={shifts} onClose={() => setDraft(null)} />
      </div>
    </div>
  );
}
